import { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { getSearchParams } from '@css-xp/ppc-utils';
import { searchContextKey, searchFieldKey } from '@css-xp/ppc-search-form';
import { useTotalCountQueryConfig } from './use-total-count-query-config';

export interface useTotalCountQueryTypes {
  partnerAccountId?: string;
  isDesignIteration1Enabled?: boolean;
  getTabsQueryVariables?: Record<string, () => void>;
}

export const useTotalCountQuery = (data: useTotalCountQueryTypes) => {
  const { isDesignIteration1Enabled } = data;
  const { txaId } = useParams<{ txaId: string }>();
  const queryString = getSearchParams(searchFieldKey) ?? '';
  const partnerSearchContext = getSearchParams(searchContextKey) ?? '';
  const [loading, setLoading] = useState(false);
  const [totalCountPerTab, setTotalCountPerTab] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const { queryConfigs } = useTotalCountQueryConfig({
    queryString,
    txaId,
    partnerSearchContext,
    isOrgSearchEnabled: true,
    partnerAccountId: data?.partnerAccountId,
    getTabsQueryVariables: data?.getTabsQueryVariables,
  });

  const getTotalCount = () => {
    setLoading(true);
    Promise.allSettled(
      queryConfigs.map(async ({ query, client, variables, getVariables }) => {
        const queryVariables = getVariables ? await getVariables() : variables;
        if (queryVariables === null) {
          return Promise.resolve({ data: { totalCount: 0 }, errors: [] });
        }
        return client.query({
          query,
          variables: queryVariables,
          fetchPolicy: 'network-only',
        });
      })
    )
      .then((results) => {
        const totalCountPerTab: Record<string, number> = results.reduce((obj, tabResult, index) => {
          const { resultKey, tabName, parentKey, getTotalCount } = queryConfigs[index] || {};

          //if the query failed, set the tabname to null
          if (tabResult.status === 'rejected') {
            return { ...obj, [tabName]: null };
          }

          //if the query is successful, set the tab name to the total count from the result and set it to 0 if the result is null.
          const result = tabResult.value;
          return {
            ...obj,
            [tabName]:
              getTotalCount?.(result.data) ||
              (parentKey ? result.data?.[parentKey]?.[resultKey]?.totalCount : result.data?.[resultKey]?.totalCount) ||
              0,
            //if parentKey is present then use parentKey to get the resultKey
          };
        }, {});

        // calculate the total count from all tabs
        const totalCount = Object.values(totalCountPerTab).reduce((sum, count) => sum + (count ?? 0), 0);

        setTotalCountPerTab(totalCountPerTab);
        setTotalCount(totalCount);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (isDesignIteration1Enabled) {
      getTotalCount();
    }
  }, [queryString]);

  if (!isDesignIteration1Enabled) {
    return { totalCountLoading: false, totalCountPerTab: {}, totalCount: 0 };
  }

  return { totalCountLoading: loading, totalCountPerTab, totalCount };
};

export default useTotalCountQuery;
